
import { Component, Prop, PropSync, Ref, Vue } from 'vue-property-decorator';
import { VForm } from 'vuetify/lib';
import { getAffiliateSearch } from '@/api/user';
import FormModal from '@/components/base/FormModal.vue';
import BaseAutocomplete from '@/components/base/design/BaseAutocomplete.vue';
import { showServerError } from '@/utils';
import Loader from '@/components/base/Loader.vue';
import BaseDateTimePicker from '@/components-UI/BaseDateTimePicker.vue';
import TrashBtn from '@/components/base/buttons/TrashBtn.vue';
import SvgExpandMore from '@/assets/icons/expand-more.svg';
import AutocompleteListItem from "@/components/base/form/AutocompleteListItem.vue";
import { NotificationMessage } from "@/utils/notification";
import { getEmployeeMethods, postAffiliateMethod } from '@/api/payout';
import { formatPhoneInput, translatePaymentsRequisite } from '@/utils/translate';
import { sortPayoutMethodFields } from '@/utils/payoutMethodFields';
import { isContractPayoutMethod } from '@/utils/payoutContractMethods';
import MultiselectDesign from '@/components/base/form/MultiselectDesign.vue';
import SelectDesign from '@/components/base/form/SelectDesign.vue';
import { IEmployeeMethod } from '@/api/types/payout';
import { searchWebmasters } from '@/api/org-cabinets-backend';
import BaseSelect from "@/components/base/design/BaseSelect.vue";
import { subYears } from 'date-fns';

interface IRules {
    [key: string]: {
        [key: string]: ((v: string) => boolean | string)[];
    };
}

@Component({
    components: {
        BaseSelect,
        AutocompleteListItem,
        BaseAutocomplete,
        FormModal,
        Loader,
        BaseDateTimePicker,
        TrashBtn,
        SvgExpandMore,
        MultiselectDesign,
        SelectDesign,
    },
    methods: {
        subYears,
    },
})
export default class FormMethod extends Vue {
    @Prop() defaultAffiliate!: string;
    @PropSync('showModal') showModalSync!: boolean;

    @Ref() affiliateInput!: BaseAutocomplete;
    @Ref() form!: typeof VForm;

    getAffiliateSearch = getAffiliateSearch;
    searchWebmasters = searchWebmasters;

    loading = false;
    offer: string | number = '';
    methods: IEmployeeMethod[] = [];
    affiliates: number[] = [];
    method = '';
    fields: any[] = [];
    isMakeDefault = false;
    valid = false;

    mounted(): void {
        if (!!this.defaultAffiliate) {
            this.affiliates.push(+this.defaultAffiliate);
        }
    }

    get isDisabledSaveBtn(): boolean {
        return !this.valid;
    }

    get currentMethod(): IEmployeeMethod | undefined {
        return this.methods.find((m) => m.id === this.method);
    }

    rules(): IRules {
        const required = (v: string): boolean | string => !!v || 'Поле обязательно для заполнения';
        const onlyLetters = (v: string): boolean | string => /^\p{Letter}+$/u.test(v) || 'Поле должно состоять из букв';
        const cardNumber = (v: string): boolean | string => /^\d{14,18}$/.test(v) || 'Поле должно состоять из 14-18 цифр';
        const dateDDMMYYYY = (v: string): boolean | string => /^\d{2}-\d{2}-\d{4}$/.test(v) || 'Введите дату в формате ДД-ММ-ГГГГ';
        const onlyEmail = (v: string): boolean | string => /^.*@.*\..*$/.test(v) || 'Поле должно содержать корректный email';
        const phone = (v: string): boolean | string => /^\+\d{11,12}$/.test((v || '').replace(/[^\d+]/g, '')) || 'Телефон должен начинаться с + и содержать от 12 до 13 символов (только цифры)';
        const length4to50 = (v: string): boolean | string => (v.length >= 4 && v.length <= 50) || 'Поле должно содержать от 4 до 50 символов';

        const rules = {
            adsfin_v1: {
                account_id: [],
            },
            bank_card_europe_v1: {
                card_number: [cardNumber],
                card_expiration: [
                    (v: string): boolean | string => /^\d{2}\/\d{2}$/.test(v) || 'Введите дату в формате ММ/ГГ',
                ],
                cardholder_name: [onlyLetters],
                cardholder_lastname: [onlyLetters],
                cardholder_birthday: [dateDDMMYYYY],
                cardholder_address: [],
                cardholder_city: [],
                cardholder_country: [],
            },
            bank_card_ru_v1: {
                card_number: [cardNumber],
                cardholder_name: [length4to50],
                phone: [phone],
                bank_name: [length4to50],
            },
            bank_card_ua_v1: {
                card_number: [cardNumber],
            },
            capitalist_kz_v1: {
                account_id: [],
            },
            capitalist_v1: {
                account_id: [],
            },
            contract_eur_individual_v1: {
                address: [],
                code: [],
                IBAN: [],
                SWIFT: [],
            },
            contract_eur_legal_v1: {
                name: [],
                vat: [],
                address: [],
                reg_number: [],
                IBAN: [],
                SWIFT: [],
            },
            contract_v1: {
                legal_id: [],
            },
            custom_v1: {},
            dmp_one_v1: {
                email: [onlyEmail],
            },
            facebook_cabinet_v1: {
                cabinet_id: [],
            },
            getuniq_v1: {
                cabinet_id: [],
            },
            google_cabinet_v1: {
                cabinet_id: [],
            },
            mytarget_cabinet_v1: {
                cabinet_id: [],
            },
            payoneer_v1: {
                IBAN: [],
            },
            paypal_v1: {
                email: [onlyEmail],
            },
            qiwi_v1: {
                account_id: [],
            },
            tether_v1: {
                account_id: [],
            },
            traffic_bank_v1: {
                cabinet_id: [],
            },
            transfer_v1: {
                IBAN: [],
            },
            vk_cabinet_v1: {
                cabinet_id: [],
            },
            webmoney_v1: {
                account_id: [
                    (v: string): boolean | string => /^[a-zA-Z]\d{12}$/.test(v) || 'Поле должно состоять из латинской буквы и 12 цифр',
                ],
            },
            yandex_cabinet_v1: {
                cabinet_id: [],
            },
            yandex_v1: {
                account_id: [],
            },
            yoomoney_v1: {
                account_id: [],
            },
            zaleycash_cabinet_v1: {
                cabinet_id: [],
            },
            zvonobot_v1: {
                account_id: [],
            },
            zvonok_v1: {
                cabinet_id: [],
            },
        };

        for (const method of Object.keys(rules)) {
            for (const rule of Object.keys(rules[method])) {
                rules[method][rule].unshift(required);
            }
        }

        return rules;
    }

    placeholders = {
        bank_card_europe_v1: {
            card_expiration: '00/00',
        },
    };
    /**
     * Телефона здесь нет намеренно: v-mask подставляет цифры в маску как есть,
     * поэтому вставленный 89017623482 превращался в +7 (890) 176-23-48 -
     * ведущая 8 занимала код города, а последняя цифра терялась.
     * Телефон форматируется в onFieldInput через formatPhoneInput.
     */
    masks = {
        bank_card_europe_v1: {
            card_expiration: '##/##',
        },
    };

    /**
     * Телефон форматируем сами - см. комментарий у masks.
     * Значение правим прямо в input и заново отдаём событие, чтобы Vuetify
     * подхватил отформатированное значение: иначе он перерисует поле из своего
     * lazyValue и лишний символ вернётся, хотя в модели его уже нет.
     * Повторный вызов на этом же значении ничего не меняет и цикл обрывается.
     */
    onFieldInput(field: any, event: Event): void {
        if (field.id !== 'phone') return;

        const input = event.target as HTMLInputElement;
        const formatted = formatPhoneInput(input.value);

        field.value = formatted;
        if (input.value === formatted) return;

        input.value = formatted;
        input.dispatchEvent(new Event('input', { bubbles: true }));
    }

    getPaymentMethodsTemplate(item: any): string {
        return item.name.ru;
    }

    getAffiliateSearchTemplate(item: any): string {
        return `${item.id} ${item.first_name} ${item.last_name}`;
    }

    makeCurrentFields(): void {
        if (!this.method) {
            this.fields = [];
            return;
        }

        if (this.currentMethod) {
            const fields = sortPayoutMethodFields([
                ...this.currentMethod?.partner_fields_keys,
                ...this.currentMethod?.manager_fields_keys,
            ], this.method);
            this.fields = fields.map((f: string) => ({
                id: f,
                name: translatePaymentsRequisite(f, this.currentMethod?.id),
                value: '',
                rules: this.rules()[this.method][f],
                placeholder: this.placeholders[this.method]?.[f],
                mask: this.masks[this.method]?.[f],
            }));
        } else {
            this.fields = [];
        }
    }

    closeModal(): void {
        this.clearInputs();
        this.showModalSync = false;
    }

    updateAffiliates(affiliates: number[]): void {
        this.affiliates = affiliates;
    }

    updateMethod(method: string): void {
        this.method = method;
        this.makeCurrentFields();
    }

    clearInputs(): void {
        this.affiliateInput.clearModel();
        this.affiliates = [];
        this.method = '';
        this.fields = [];
        this.isMakeDefault = false;
    }

    getFieldsByFieldsKeys(key: string): { [key: string]: string } {
        return Object.fromEntries(
            this.fields
                .filter((f) => this.currentMethod?.[key]?.includes(f.id))
                .map((f) => {
                    if (f.id === 'card_expiration') {
                        // 02/30 => 2030-02
                        return [f.id, `20${f.value.split('/')[1]}-${f.value.split('/')[0]}`];
                    }
                    if (f.id === 'phone') {
                        // +7 (912) 345-67-89 => +79123456789
                        return [f.id, f.value.replace(/[^\d+]/g, '')];
                    }
                    return [f.id, f.value];
                }),
        );
    }

    async createRequestMethod(): Promise<void> {
        // @ts-ignore
        this.$refs.form.validate();
        if (!this.valid) return;

        this.loading = true;
        try {
            const data = {
                partner_id: this.affiliates,
                method_id: this.method,
                partner_data: this.getFieldsByFieldsKeys('partner_fields_keys'),
                manager_data: this.getFieldsByFieldsKeys('manager_fields_keys'),
                is_default: this.isMakeDefault,
            };

            await postAffiliateMethod(data);

            await this.$emit('edit');
            this.showNotification();
            this.clearInputs();
            this.closeModal();
        } catch (err) {
            showServerError(err, 'Способ выплат не создан');
        } finally {
            this.loading = false;
        }
    }

    showNotification(): void {
        const notificationText = `Способ выплат успешно создан`;
        new NotificationMessage(notificationText, 'success' );
    }

    async created(): Promise<void> {
        try {
            const { data } = await getEmployeeMethods({ is_active: 1 });
            // договорные способы создаются автоматически через 1С
            this.methods = data.filter((m) => !isContractPayoutMethod(m.id));
        } catch (err) {
            showServerError(err, 'Список методов не загружен');
        }
    }
}
